'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { stripInvisibles } from '@workspace/constants';
import { ProductDetailLevel3Data, ProductVariant } from '../../interfaces';

export interface UseProductDetailTemplateResult {
  selectedVariant: ProductVariant | null;
  /** True only when the current filters narrow down to exactly one variant. */
  isExactMatch: boolean;
  selectedAttributes: Record<string, string>;
  carouselKey: number;
  /** Filtered to variants matching the current attribute selection. Used by the carousel. */
  carouselImages: Array<{
    _key: string;
    alt?: string;
    bynderImage?: {
      alt?: string;
    };
    asset: {
      _ref?: string;
      url: string;
    };
    hotspot?: unknown;
    crop?: unknown;
  }>;
  /** Full unfiltered image set. Used by the fullscreen gallery. */
  galleryImages: Array<{
    _key: string;
    alt?: string;
    bynderImage?: {
      alt?: string;
    };
    asset: {
      _ref?: string;
      url: string;
    };
    hotspot?: unknown;
    crop?: unknown;
  }>;
  handleAttributeChange: (attributeId: string, value: string) => void;
  getAvailableValues: (attributeId: string) => string[];
  getAllPossibleValues: (attributeId: string) => string[];
}

type ProductImage = NonNullable<ProductVariant['variantImages']>[number];

type MatchResult = {
  matched: ProductVariant[];
  selectedVariant: ProductVariant | null;
};

const findMatchingVariants = (
  variants: ProductVariant[],
  selectedAttributes: Record<string, string>,
  requiredAttributeIds: string[],
): MatchResult => {
  const allRequiredSelected = requiredAttributeIds.every((id) => Boolean(selectedAttributes[id]));

  if (allRequiredSelected) {
    const matched = variants.filter((variant) =>
      requiredAttributeIds.every((attrId) => {
        const attrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
        return attrValue && stripInvisibles(attrValue.value).trim() === selectedAttributes[attrId];
      }),
    );
    return { matched, selectedVariant: matched[0] ?? null };
  }

  const matched = variants.filter((variant) =>
    Object.entries(selectedAttributes).every(([attrId, selectedValue]) => {
      if (!selectedValue) return true;
      const attrValue = variant.attributeValues.find((av) => av.attribute._ref === attrId);
      return attrValue && stripInvisibles(attrValue.value).trim() === selectedValue;
    }),
  );
  return { matched, selectedVariant: matched[0] ?? null };
};

const buildImages = (
  allVariants: ProductVariant[],
  matchingVariants: ProductVariant[],
  selectedVariant: ProductVariant | null,
  primaryImage: ProductDetailLevel3Data['primaryImage'],
  productImages: ProductDetailLevel3Data['productImages'],
): ProductImage[] => {
  const hasActiveFilters = matchingVariants.length > 0;
  const variantsToShow = hasActiveFilters ? matchingVariants : allVariants;

  const images: ProductImage[] = [];
  const seen = new Set<string>();
  const pushUnique = (img?: ProductImage) => {
    if (!img) return;
    const id = img.asset._ref || img.asset.url;
    if (!id || seen.has(id)) return;
    seen.add(id);
    images.push(img);
  };

  if (selectedVariant?.variantImages && selectedVariant.variantImages.length > 0) {
    selectedVariant.variantImages.forEach((img, index) => {
      pushUnique({ ...img, _key: img._key || `selected-variant-${index}` });
    });

    if (!hasActiveFilters && primaryImage) {
      pushUnique({
        _key: 'primary',
        alt: primaryImage.alt,
        bynderImage: primaryImage.bynderImage,
        asset: primaryImage.asset,
        hotspot: primaryImage.hotspot,
        crop: primaryImage.crop,
      });
    }

    variantsToShow.forEach((variant, vIdx) => {
      if (variant === selectedVariant || !variant.variantImages?.length) return;
      variant.variantImages.forEach((img, iIdx) => {
        pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
      });
    });
  } else {
    if (!hasActiveFilters && primaryImage) {
      pushUnique({
        _key: 'primary',
        alt: primaryImage.alt,
        bynderImage: primaryImage.bynderImage,
        asset: primaryImage.asset,
        hotspot: primaryImage.hotspot,
        crop: primaryImage.crop,
      });
    }

    variantsToShow.forEach((variant, vIdx) => {
      if (!variant.variantImages?.length) return;
      variant.variantImages.forEach((img, iIdx) => {
        pushUnique({ ...img, _key: img._key || `variant-${vIdx}-${iIdx}` });
      });
    });
  }

  if (!hasActiveFilters && productImages && productImages.length > 0) {
    productImages.forEach((img, pIdx) => pushUnique({ ...img, _key: img._key || `product-${pIdx}` }));
  }

  return images;
};

export const useProductDetailTemplate = (product: ProductDetailLevel3Data): UseProductDetailTemplateResult => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [matchingVariants, setMatchingVariants] = useState<ProductVariant[]>([]);
  const [matchingVariantsCount, setMatchingVariantsCount] = useState(0);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [carouselKey, setCarouselKey] = useState(0);

  // Reset carousel to first image when variant changes
  useEffect(() => {
    setCarouselKey((prev) => prev + 1);
  }, [selectedVariant]);

  const carouselImages = useMemo(
    () =>
      buildImages(
        product.productVariants,
        matchingVariants,
        selectedVariant,
        product.primaryImage,
        product.productImages,
      ),
    [product.primaryImage, product.productImages, product.productVariants, selectedVariant, matchingVariants],
  );

  const galleryImages = useMemo(
    () => buildImages(product.productVariants, [], selectedVariant, product.primaryImage, product.productImages),
    [product.primaryImage, product.productImages, product.productVariants, selectedVariant],
  );

  const getAllPossibleValues = useCallback(
    (attributeId: string): string[] => {
      const values = new Set<string>();

      product.productVariants.forEach((variant) => {
        const attrValue = variant.attributeValues?.find((av) => av.attribute._ref === attributeId);
        if (attrValue && attrValue.value) {
          const cleanValue = stripInvisibles(attrValue.value).trim();
          if (cleanValue) {
            values.add(cleanValue);
          }
        }
      });

      return Array.from(values).sort((a, b) => {
        // Sort dimensions first
        const aDimensionMatch = a.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/i);
        const bDimensionMatch = b.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/i);

        if (aDimensionMatch && bDimensionMatch) {
          const aWidth = parseFloat(aDimensionMatch[1]);
          const aHeight = parseFloat(aDimensionMatch[2]);
          const bWidth = parseFloat(bDimensionMatch[1]);
          const bHeight = parseFloat(bDimensionMatch[2]);

          if (aWidth !== bWidth) {
            return aWidth - bWidth;
          }
          return aHeight - bHeight;
        }

        if (aDimensionMatch) return -1;

        if (bDimensionMatch) return 1;

        const aNum = parseFloat(a);
        const bNum = parseFloat(b);

        // Sort numbers second
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        if (!isNaN(aNum)) return -1;
        if (!isNaN(bNum)) return 1;

        // Sort text last
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      });
    },
    [product.productVariants],
  );

  const getAvailableValues = useCallback(
    (attributeId: string): string[] => {
      const values = new Set<string>();

      const allPossibleValues = getAllPossibleValues(attributeId);

      allPossibleValues.forEach((value) => {
        const hasCompatibleVariant = product.productVariants.some((variant) => {
          const variantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === attributeId);
          if (!variantAttrValue || stripInvisibles(variantAttrValue.value).trim() !== value) {
            return false;
          }

          return Object.entries(selectedAttributes).every(([selectedAttrId, selectedValue]) => {
            if (selectedAttrId === attributeId) return true;
            if (!selectedValue) return true;

            const otherVariantAttrValue = variant.attributeValues.find((av) => av.attribute._ref === selectedAttrId);
            if (!otherVariantAttrValue) return false;

            return stripInvisibles(otherVariantAttrValue.value).trim() === selectedValue;
          });
        });

        if (hasCompatibleVariant) {
          values.add(value);
        }
      });

      return Array.from(values);
    },
    [getAllPossibleValues, product.productVariants, selectedAttributes],
  );

  const requiredAttributeIds = useMemo(() => {
    if (product.attributeFilters && product.attributeFilters.length > 0) {
      return product.attributeFilters.map((f) => f.attribute._ref);
    }
    const ids = new Set<string>();
    product.productVariants.forEach((v) => v.attributeValues.forEach((av) => ids.add(av.attribute._ref)));
    return Array.from(ids);
  }, [product.attributeFilters, product.productVariants]);

  const handleAttributeChange = useCallback(
    (attributeId: string, value: string) => {
      const newSelectedAttributes: Record<string, string> = {
        ...selectedAttributes,
        [attributeId]: value,
      };

      if (!value) {
        delete newSelectedAttributes[attributeId];
      }

      setSelectedAttributes(newSelectedAttributes);

      if (Object.keys(newSelectedAttributes).length === 0) {
        setSelectedVariant(null);
        setMatchingVariants([]);
        setMatchingVariantsCount(0);
        return;
      }

      const { matched, selectedVariant: matchingVariant } = findMatchingVariants(
        product.productVariants,
        newSelectedAttributes,
        requiredAttributeIds,
      );

      setMatchingVariantsCount(matched.length);
      setMatchingVariants(matched);
      setSelectedVariant(matchingVariant);
    },
    [product.productVariants, requiredAttributeIds, selectedAttributes],
  );

  return {
    selectedVariant,
    isExactMatch: matchingVariantsCount === 1,
    selectedAttributes,
    carouselKey,
    carouselImages,
    galleryImages,
    handleAttributeChange,
    getAvailableValues,
    getAllPossibleValues,
  };
};
